import { useActions, useValues } from '@core/store'
import type { TAudioSimple, TPlaylist } from '@core/models'

import { audioPlayerStore } from '../store/audioPlayerStore'
import { useAudioRef } from '../providers/AudioRefProvider'

import { useActiveAudio } from './useActiveAudio'

type TUseAudioPlaybackResult = {
    isActiveAudio: boolean
    isPlaying: boolean
    /** Toggle play/pause for this track (activating it first if needed). Analytics live in the caller. */
    playPause: () => void
    playFromSeconds: (seconds: number) => void
}

/**
 * Per-track playback trigger over the shared player built in MI2-2026. Reports whether `audio` is the
 * active track and seeks to a position, activating the track first when it is not yet playing.
 * `playlist` defaults to a single-item playlist built from `audio`. The container owns analytics.
 */
export const useAudioPlayback = (
    audio: TAudioSimple,
    playlist?: TPlaylist,
): TUseAudioPlaybackResult => {
    const audioRef = useAudioRef()
    const activeAudio = useActiveAudio()
    const [isPlaying] = useValues([audioPlayerStore.props.getIsPlaying])
    const { setPlaylist, playAudio, setIsPlaying } = useActions({
        setPlaylist: audioPlayerStore.actions.setPlaylist,
        playAudio: audioPlayerStore.actions.playAudio,
        setIsPlaying: audioPlayerStore.actions.setIsPlaying,
    })

    const isActiveAudio = activeAudio?.id === audio.id

    const activate = () => {
        if (playlist && playlist.items.length > 0) {
            setPlaylist(playlist)
            const index = playlist.items.findIndex(item => item.id === audio.id)
            playAudio(index >= 0 ? index : 0)

            return
        }

        setPlaylist({ id: audio.id, items: [audio], total: 1, pages: 1, page: 1, url: '' })
        playAudio(0)
    }

    const playPause = () => {
        if (isActiveAudio) {
            setIsPlaying(!isPlaying)
        } else {
            activate()
        }
    }

    const playFromSeconds = (seconds: number) => {
        if (!isActiveAudio) {
            activate()
        } else if (!isPlaying) {
            setIsPlaying(true)
        }

        // Defer so the store update points the shared <audio> at the track before we set its time.
        setTimeout(() => {
            if (audioRef.current) {
                audioRef.current.currentTime = seconds
            }
        }, 0)
    }

    return {
        isActiveAudio,
        isPlaying: isActiveAudio && isPlaying,
        playPause,
        playFromSeconds,
    }
}
